<template>
  <div>
    <v-dialog v-model="dialog" persistent :max-width="$vuetify.breakpoint.mobile ? '95vw' : '800'">
      <v-card>
        <v-toolbar color="primary" elevation="0">
          <v-toolbar-title class="white--text">{{ $t('dueDiligence.confirmation') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn color="white" icon @click="closeModal">
            <v-icon style="color:white !important">mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-5 black--text">
          <div v-if="checkingCompliance" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" class="mb-2"></v-progress-circular>
            <div class="text-body-2">{{ $t('dueDiligence.checkingComplianceStatus') }}</div>
          </div>

          <div v-else class="mb-4">
            <h3 class="mb-3">{{ $t('dueDiligence.approvalConfirmationMessage') }}</h3>
            <div v-if="hasNonCompliantFarms" class="mb-4">
              <div class="warning-banner">
                <div class="warning-title">{{ $t('dueDiligence.nonCompliantFarmsDetected') }}</div>
              </div>
              <h4 class="warning-text mt-5">{{ $t('dueDiligence.approvalConfirmationMessage') }}</h4>
            </div>

            <div class="mb-4">
              <p class="mb-2 font-weight-medium">{{ $t('dueDiligence.approvalOnceApprovedTitle') }}:</p>
              <ul class="ml-4">
                <li v-if="hasNonCompliantFarms">{{ approvalNonCompliantMessage }}</li>
                <li v-if="isKenyaClient">{{ $t('dueDiligence.approvalVerifiedByAgent') }}</li>
                <li v-else>{{ $t('dueDiligence.approvalVerifiedByPtsi') }}</li>
                <li>{{ $t('dueDiligence.approvalVerificationLinks') }}</li>
                <li>{{ $t('dueDiligence.approvalNoFurtherChanges') }}</li>
                <li>{{ $t('dueDiligence.approvalPublishedToBlockchain') }}</li>
              </ul>
            </div>

            <template>
              <v-divider class="my-4"></v-divider>

              <div class="mb-4">
                <v-checkbox
                  v-model="isTemporaryApproval"
                  color="primary"
                  class="mt-0 pt-0"
                >
                  <template v-slot:label>
                    <div>
                      <span class="font-weight-medium black--text">{{ $t('dueDiligence.temporaryApprovalLabel') }}</span>
                      <br>
                      <span class="text-caption black--text">{{ $t('dueDiligence.temporaryApprovalDescription') }}</span>
                    </div>
                  </template>
                </v-checkbox>
              </div>

              <div class="mb-4" v-if="isTemporaryApproval">
                <div class="d-flex align-center mb-2">
                  <span class="font-weight-medium mr-2">{{ $t('dueDiligence.approvalExpirationPeriod') }}</span>
                  <v-tooltip top color="primary" max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon size="16" color="primary" v-bind="attrs" v-on="on">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    <span>{{ $t('dueDiligence.approvalExpirationDays') }}</span>
                  </v-tooltip>
                </div>

                <v-row no-gutters>
                  <v-col cols="4">
                    <v-text-field
                      v-model="approvalExpirationValue"
                      type="number"
                      min="1"
                      outlined
                      dense
                      class="mr-2"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="8">
                    <v-select
                      v-model="approvalExpirationUnit"
                      :items="expirationUnits"
                      item-text="text"
                      item-value="value"
                      outlined
                      dense
                    ></v-select>
                  </v-col>
                </v-row>
              </div>
            </template>

            <!-- PTSI: reviewer signature + fixed authorized signatory (Legal Proof) -->
            <template v-if="isPtsiClient">
              <v-divider class="my-4"></v-divider>
              <section class="signature-section">
                <div class="reviewer-card">
                  <div class="section-grid">
                    <div class="field">
                      <span class="label">{{ $t('dueDiligence.reviewerName') }}</span>
                      <span class="value">{{ reviewerName }}</span>
                    </div>
                    <div class="field">
                      <span class="label">{{ $t('dueDiligence.reviewerEmail') }}</span>
                      <span class="value">{{ reviewerEmail }}</span>
                    </div>
                    <div class="field full digital-signature-field">
                      <span class="label">{{ $t('dueDiligence.digitalSignature') }}</span>
                      <div v-if="signaturePreviewUrl" class="signatory-signature-preview">
                        <img :src="signaturePreviewUrl" :alt="$t('dueDiligence.approvalSignaturePreview')" />
                        <v-btn small text color="primary" class="mt-1" @click="openSignatureModal = true">
                          <v-icon left small>mdi-draw</v-icon>
                          {{ $t('dueDiligence.changeSignature') }}
                        </v-btn>
                      </div>
                      <div v-else class="digital-signature-empty">
                        <span class="value pending-signature">{{ $t('dueDiligence.digitalSignatureLater') }}</span>
                        <v-btn
                          color="primary"
                          depressed
                          small
                          class="mt-2"
                          @click="openSignatureModal = true"
                        >
                          <v-icon :style="{ color: 'white !important' }" left small>mdi-draw</v-icon>
                          {{ $t('dueDiligence.addSignature') }}
                        </v-btn>
                      </div>
                      <p v-if="signatureError" class="signature-error mt-2 mb-0">{{ signatureError }}</p>
                    </div>
                  </div>
                  <p class="section-hint reviewer-signature-hint mb-0">
                    {{ $t('dueDiligence.ptsiReviewerSignatureHint') }}
                  </p>
                </div>

                <div class="signatory-card">
                  <h4 class="reviewer-title">{{ $t('dueDiligence.ptsiAuthorizedSignatoryTitle') }}</h4>
                  <p class="section-hint">{{ $t('dueDiligence.ptsiApproveOnBehalfHint') }}</p>
                  <div class="section-grid">
                    <div class="field">
                      <span class="label">{{ $t('dueDiligence.signatoryName') }}</span>
                      <span class="value">{{ ptsiSignatory.name }}</span>
                    </div>
                    <div class="field">
                      <span class="label">{{ $t('dueDiligence.signatoryPosition') }}</span>
                      <span class="value">{{ ptsiSignatory.position }}</span>
                    </div>
                    <div class="field full">
                      <span class="label">{{ $t('dueDiligence.signatoryCompany') }}</span>
                      <span class="value">{{ ptsiSignatory.company }}</span>
                    </div>
                  </div>
                </div>
              </section>
            </template>
          </div>
        </v-card-text>

        <v-card-actions class="px-6 pb-4 approval-actions">
          <p v-if="isPtsiClient && !canApproveWithSignature" class="approve-hint mb-0">
            {{ $t('dueDiligence.approvalSignatureRequiredError') }}
          </p>
          <v-spacer></v-spacer>
          <v-btn outlined color="primary" @click="closeModal" :disabled="checkingCompliance">
            {{ $t('cancel') }}
          </v-btn>
          <v-tooltip top :disabled="!isPtsiClient || canApproveWithSignature">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" class="d-inline-block">
                <v-btn
                  depressed
                  color="primary"
                  @click="confirmApproval"
                  :disabled="
                    checkingCompliance
                    || (isTemporaryApproval && !isExpirationValid)
                    || (isPtsiClient && !canApproveWithSignature)
                  "
                >
                  {{ $t('dueDiligence.approvalApproveButton') }}
                </v-btn>
              </span>
            </template>
            <span>{{ $t('dueDiligence.approvalSignatureRequiredError') }}</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <signature-modal
      :dialog="openSignatureModal"
      upload-folder="dds-approval-signatures"
      @closeSignatureModel="openSignatureModal = false"
      @handleSignature="handleSignatureCaptured"
    />
  </div>
</template>

<script>
import DeforestationService from '@/_services/DeforestationService';
import UserService from '@/_services/UserService';
import S3UploadService from '@/_services/S3UploadService';
import LoadingMixin from '@/mixins/LoadingMixin';
import { isIndonesianClient } from '@/utils';
import { ROLES } from '../../../../../constants/roles';
import SignatureModal from '@/views/deforestation/dds/reports/components/risk-assessment/SignatureModal.vue';

const PTSI_AUTHORIZED_SIGNATORY = {
  name: 'Rosikh Adicandra',
  position: 'VP BDSNE',
  company: 'PT Surveyor Indonesia',
};

export default {
  name: 'ApprovalConfirmationModal',
  mixins: [LoadingMixin],
  components: {
    SignatureModal,
  },
  props: {
    dialog: {
      type: Boolean,
      required: true
    },
    selectedReportsCount: {
      type: Number,
      default: 0
    },
    selectedReportIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      isTemporaryApproval: false,
      approvalExpirationValue: 30,
      approvalExpirationUnit: 'days',
      expirationUnits: [
        { text: this.$t('dueDiligence.approvalExpirationDays'), value: 'days' },
        { text: this.$t('dueDiligence.approvalExpirationWeeks'), value: 'weeks' },
        { text: this.$t('dueDiligence.approvalExpirationMonths'), value: 'months' }
      ],
      settings: null,
      hasNonCompliantFarms: false,
      checkingCompliance: false,
      openSignatureModal: false,
      pendingSignatureKey: null,
      pendingSignatureUrl: null,
      signatureError: '',
    }
  },
  computed: {
    ptsiSignatory() {
      return PTSI_AUTHORIZED_SIGNATORY;
    },
    currentUser() {
      return this.$store.getters.getUser || {};
    },
    reviewerName() {
      const user = this.currentUser;
      const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
      return name || user.name || '-';
    },
    reviewerEmail() {
      return this.currentUser.email || this.currentUser.unverifiedEmail || '-';
    },
    signaturePreviewUrl() {
      return this.pendingSignatureUrl;
    },
    canApproveWithSignature() {
      return !!this.pendingSignatureKey;
    },
    getOrganizationId() {
      return this.$store.getters.getUser?.user_organization?.id;
    },
    isKenyaClient(){
      const user = JSON.parse(localStorage.getItem('user'));
      if(!user) return false;
      return user.user_role_assoc.some(role => role.id === ROLES.kenya_ptsi.value || role.id === ROLES.kenya_ptsi_worker.value);
    },
    approvalNonCompliantMessage() {
      const key = isIndonesianClient()
        ? 'dueDiligence.approvalCompliantAndRiskDetected'
        : 'dueDiligence.approvalCompliantAndManuallyMitigated';
      return this.$t(key);
    },
    isPtsiClient() {
      return isIndonesianClient();
    },
    isExpirationValid() {
      if (!this.isTemporaryApproval) return true;
      return parseInt(this.approvalExpirationValue, 10) > 0;
    },
  },
  methods: {
    persistSignatureOnUser(signatureS3Key, signatureUrl) {
      if (!signatureS3Key) return;
      try {
        const stored = JSON.parse(localStorage.getItem('user') || '{}') || {};
        const updatedUser = {
          ...stored,
          ...this.currentUser,
          signatureS3Key,
          signatureUrl: signatureUrl || stored.signatureUrl || this.currentUser.signatureUrl || null,
        };
        this.$store.commit('setUserOnly', updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } catch (error) {
        console.error('Failed to persist signature on user:', error);
      }
    },
    applyStoredSignature(signatureS3Key, signatureUrl) {
      if (!signatureS3Key) return;
      this.pendingSignatureKey = signatureS3Key;
      this.pendingSignatureUrl = signatureUrl || this.pendingSignatureUrl;
    },
    async loadStoredSignature() {
      const fromUser = this.currentUser || {};
      let key = fromUser.signatureS3Key || null;
      let url = fromUser.signatureUrl || null;
      this.applyStoredSignature(key, url);

      try {
        const response = await UserService.getMySignature();
        const stored = response?.data || {};
        if (stored.signatureS3Key) {
          key = stored.signatureS3Key;
          url = stored.signatureUrl || url;
        }
      } catch (error) {
        console.error('Failed to load stored signature:', error);
      }

      if (!this.dialog || !key) return;

      try {
        const freshUrl = await S3UploadService.getFileUrl(key, true);
        if (freshUrl) url = freshUrl;
      } catch (error) {
        console.error('Failed to resolve signature URL:', error);
      }

      if (!this.dialog) return;
      this.applyStoredSignature(key, url);
      this.persistSignatureOnUser(key, url);
    },
    handleSignatureCaptured(payload) {
      if (!payload?.s3key) return;
      this.pendingSignatureKey = payload.s3key;
      this.pendingSignatureUrl = payload.attachmentLink || null;
      this.signatureError = '';
      this.openSignatureModal = false;
      this.saveSignatureToAccount(payload.s3key, payload.attachmentLink || null);
    },
    async saveSignatureToAccount(signatureS3Key, signatureUrl) {
      if (!signatureS3Key || !signatureUrl) return;
      try {
        await UserService.updateMySignature({ signatureS3Key, signatureUrl });
        this.persistSignatureOnUser(signatureS3Key, signatureUrl);
      } catch (error) {
        console.error('Failed to save signature to account:', error);
      }
    },
    async fetchSettings() {
      this.startLoading();
      try {
        const response = await DeforestationService.getApprovalFlowSettingByOrgId();
        if (response.data?.approvalFlowSettingByOrgId) {
          this.settings = response.data.approvalFlowSettingByOrgId;
          this.approvalExpirationValue = parseInt(this.settings.approval_expiration_period) || 0;
          this.approvalExpirationUnit = this.settings.approval_expiration_unit?.toLowerCase() || 'days';
        }
      } catch (error) {
        console.error('Failed to fetch approval flow settings:', error);
      } finally {
        this.stopLoading();
      }
    },
    async evaluateNonCompliantFarms() {
      try {
        this.checkingCompliance = true;
        this.hasNonCompliantFarms = false;

        if (!this.selectedReportIds || !this.selectedReportIds.length) {
          this.checkingCompliance = false;
          return;
        }

        for (const id of this.selectedReportIds) {
          try {
            const reportId = parseInt(id, 10);
            if (isNaN(reportId)) {
              console.warn('Invalid report ID:', id);
              continue;
            }

            const warnings = await DeforestationService.concludeDiligenceReportByStatusWarnings(reportId);
            const values = Object.values(warnings?.data?.concludeDigilienceReportStatus || {});
            const allZero = values.length ? values.every(x => x == 0) : true;
            if (!allZero) {
              this.hasNonCompliantFarms = true;
              break;
            }
          } catch (err) {
            console.warn('Failed to fetch warnings for report', id, err);
          }
        }
      } catch (e) {
        console.warn('evaluateNonCompliantFarms failed', e);
      } finally {
        this.checkingCompliance = false;
      }
    },
    closeModal() {
      this.resetForm();
      this.$emit('close');
    },
    confirmApproval() {
      if (this.isPtsiClient && !this.pendingSignatureKey) {
        this.signatureError = this.$t('dueDiligence.approvalSignatureRequiredError');
        return;
      }

      const approvalData = {
        isTemporaryApproval: this.isTemporaryApproval,
        approvalExpirationValue: this.isTemporaryApproval ? parseInt(this.approvalExpirationValue) || 0 : null,
        approvalExpirationUnit: this.isTemporaryApproval ? this.approvalExpirationUnit : null,
        approvalSignatureS3Key: this.pendingSignatureKey || undefined,
      };

      this.$emit('confirm', approvalData);
      this.closeModal();
    },
    resetForm() {
      this.isTemporaryApproval = false;
      this.approvalExpirationValue = parseInt(this.settings?.approval_expiration_period) || 0;
      this.approvalExpirationUnit = this.settings?.approval_expiration_unit?.toLowerCase() || 'days';
      this.openSignatureModal = false;
      this.pendingSignatureKey = null;
      this.pendingSignatureUrl = null;
      this.signatureError = '';
    }
  },
  watch: {
    dialog(newVal) {
      if (newVal) {
        this.fetchSettings();
        this.evaluateNonCompliantFarms();
        this.loadStoredSignature();
      } else {
        this.resetForm();
      }
    },
    approvalExpirationValue(newVal) {
      if (newVal !== null && newVal !== undefined) {
        this.approvalExpirationValue = parseInt(newVal) || 0;
      }
    }
  }
}
</script>

<style scoped>
.v-input--checkbox .v-input__slot {
  margin-bottom: 0;
}
.warning-banner {
  background: #fff4e5;
  border: 2px solid #f5b041;
  border-radius: 6px;
  padding: 12px;
}
.warning-title {
  font-weight: 600;
  margin-bottom: 6px;
}
.warning-text {
  color: #333;
}

.signature-section {
  background: #f8fbff;
  border: 1px solid #c5d4f0;
  border-radius: 8px;
  padding: 14px;
}

.reviewer-card {
  background: #fff;
  border: 1px solid #dce8f5;
  border-radius: 6px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(13, 71, 161, 0.06);
}

.signatory-card {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed #c5d4f0;
}

.reviewer-title {
  font-size: 13px;
  font-weight: 600;
  color: #184980;
  margin: 0 0 10px;
}

.section-hint {
  font-size: 13px;
  color: #666;
  margin: 0 0 8px;
  line-height: 1.45;
}

.reviewer-signature-hint {
  margin-top: 12px;
}

.section-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.field.full {
  grid-column: 1 / -1;
}

.label {
  font-size: 12px;
  color: #607d8b;
}

.value {
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
}

.digital-signature-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.digital-signature-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.signatory-signature-preview {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 12px;
  border: 1px dashed #90a4ae;
  border-radius: 6px;
  background: #fff;
  max-width: 280px;
}

.signatory-signature-preview img {
  max-height: 100px;
  max-width: 100%;
  object-fit: contain;
}

.pending-signature {
  color: #64748b;
  font-style: italic;
}

.signature-error {
  color: #c62828;
  font-size: 13px;
}

.approve-hint {
  flex: 1 1 100%;
  color: #e65100;
  font-size: 13px;
}

.approval-actions {
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 600px) {
  .section-grid {
    grid-template-columns: 1fr;
  }
}
</style>
